/**
 * welcomeCopy.ts
 *
 * Canonical user-facing welcome / intro chat-bubble copy for the
 * GlobalChatSystem on `/desktop` and `/agent-activity`.
 *
 * Two variants:
 *
 *   - GENERIC_WELCOME — default fallback for any tenant where we don't have
 *     firm-specific tooling guidance to lean on. Mentions English/Spanish
 *     bilingual support and four neutral starter prompts.
 *
 *   - FERNANDO_WELCOME — tailored for The Law Offices of Fernando Hidalgo.
 *     Names the actual document types Fernando's team draws on (NDAs,
 *     demand letters, pre-hearing statements, I-589) and the multi-step
 *     intake → research → draft workflow.
 *
 * Starter prompts (F4-A, 2026-07-13):
 *   The four starters under "Try one of these to get started:" are the SINGLE
 *   SOURCE OF TRUTH exported as `*_WELCOME_STARTERS` arrays; the bubble body
 *   bullets are DERIVED from them (so the copy and the clickable starter chips
 *   can never drift). The GlobalChatSystem renders these as one-click chips on
 *   an empty thread — previously they only rendered as static `<li>` bullets
 *   the user had to copy/paste.
 *
 * Brand spelling note (Miles directive 2026-05-09):
 *   - "Prest0n" (capital P, zero) is the AI persona on the chat surface.
 *   - "prest0" (lowercase) is the marketing brand for the company; do NOT
 *     overwrite that anywhere off the chat surface.
 *
 * Firestore note:
 *   These strings are the in-code FALLBACK. Live tenants can still override
 *   the welcome via `tenants/{tenantId}/config/runtime.chatWelcomeMessage`
 *   or `accounts/{uid}/config/runtime.chatWelcomeMessage`. The migration
 *   script `scripts/migrate-fernando-welcome.ts` writes FERNANDO_WELCOME
 *   into Fernando's tenant config so any cached/legacy override is replaced
 *   with the new copy.
 */

/** Render a starter prompt as its welcome-bubble bullet line (`• "…"`). The
 *  surrounding quotes are DISPLAY-ONLY; the clickable chip + the sent message
 *  use the bare prompt string (see WELCOME_STARTERS arrays). */
const starterBullet = (prompt: string): string => `• "${prompt}"`;

/** Generic starter prompts — the exact text sent when a chip is clicked
 *  (no surrounding display quotes). Shared source for the body bullets. */
export const GENERIC_WELCOME_STARTERS: readonly string[] = [
  'Write a Python script to automate my task spreadsheet',
  'Draft an NDA or business agreement for [Counterparty Name]',
  'Translate this document to Spanish',
  'Read these project files and summarize the requirements',
];

/** Generic welcome copy — used for any non-Fernando tenant that has no
 *  Firestore-driven override. Stays neutral (no firm-specific tools). */
export const GENERIC_WELCOME = [
  "Hey — I'm Prest0n, your personal operations companion. I work in English and Spanish. Ask me anything, or pick a starter below.",
  '',
  'Try one of these to get started:',
  ...GENERIC_WELCOME_STARTERS.map(starterBullet),
  '',
  'Or just tell me what you need.',
].join('\n');

/** GUEST starter prompts (G1, 2026-07-15; L2 qualifying rewrite 2026-07-15) —
 *  service-oriented QUESTIONS for signed-out visitors, whose Prest0n answers
 *  about the site/services but friendly-declines doing actual work (member
 *  feature). Work-action chips ("Draft an NDA…") would invite turns the guest
 *  backend must decline. The qualifying frame (owner directive): a guest's
 *  first contact is a QUALIFY + SUBTLE CONFIDENT SELL moment — never suggest
 *  doing work, never suggest creating an account. One chip stays in the other
 *  language so bilingual visitors see themselves in the surface. */
export const GUEST_WELCOME_STARTERS: readonly string[] = [
  'What can Prest0n do for me?',
  'How does pricing work?',
  "What's included in a subscription?",
  '¿Qué puede hacer Prest0n?',
];

/** Spanish guest starters — same qualifying frame, same count (F4-A: the
 *  bubble bullets derive from whichever set is live, so copy and chips can
 *  never drift in either language). */
export const GUEST_WELCOME_STARTERS_ES: readonly string[] = [
  '¿Qué puede hacer Prest0n por mí?',
  '¿Cómo funcionan los precios?',
  '¿Qué incluye la suscripción?',
  'What can Prest0n do for me?',
];

/** Guest welcome copy (L2, owner intent 2026-07-15): 2–3 sentences — who
 *  Prest0n is, what Prest0 offers (one confident clause, not a feature dump),
 *  then a qualifying invitation. The clause is the site's own (2026-11-03): a
 *  full-time digital employee, the hero's "more expert capacity, without more
 *  headcount" (src/config/siteTagline.ts), in place of "a personal AI operations
 *  companion". Bullets derived from the guest starters
 *  (F4-A one-source rule). NEVER suggests doing work here and NEVER suggests
 *  creating an account. */
export const GUEST_WELCOME = [
  "Hey — I'm Prest0n. Prest0 gives you a full-time digital employee: more expert capacity, without more headcount — research, documents, translations and automations, all inside your own account. What brings you to Prest0 today?",
  '',
  'Curious about something? Try one of these:',
  ...GUEST_WELCOME_STARTERS.map(starterBullet),
  '',
  "Or just ask — I'm happy to answer any questions about Prest0.",
].join('\n');

/** Spanish guest welcome — natural equivalent (not a literal translation),
 *  same qualifying shape and the same F4-A chip/bullet coupling. */
export const GUEST_WELCOME_ES = [
  'Hola — soy Prest0n. Prest0 te da un empleado digital de tiempo completo: más capacidad experta, sin más personal — investigación, documentos, traducciones y automatizaciones, todo dentro de tu propia cuenta. ¿Qué te trae a Prest0 hoy?',
  '',
  '¿Tienes curiosidad? Prueba una de estas:',
  ...GUEST_WELCOME_STARTERS_ES.map(starterBullet),
  '',
  'O simplemente pregúntame — con gusto respondo cualquier duda sobre Prest0.',
].join('\n');

/**
 * Language-resolved guest welcome + starters. The signed-out gate in
 * `useTenantChatConfig` resolves through `getUserUiLanguage()` (the app's
 * canonical UI-language source) and calls these — everything else keeps
 * importing the EN constants directly.
 */
export function getGuestWelcome(language: 'en' | 'es'): string {
  return language === 'es' ? GUEST_WELCOME_ES : GUEST_WELCOME;
}

export function getGuestWelcomeStarters(language: 'en' | 'es'): string[] {
  return [...(language === 'es' ? GUEST_WELCOME_STARTERS_ES : GUEST_WELCOME_STARTERS)];
}

/** Fernando starter prompts — the exact text sent when a chip is clicked
 *  (no surrounding display quotes). Shared source for the body bullets. */
export const FERNANDO_WELCOME_STARTERS: readonly string[] = [
  "Research California workers' comp filing deadlines",
  'Draft an NDA for [Counterparty Name]',
  'Translate this letter to Spanish',
  'Read these intake documents and summarize the issues',
];

/** Fernando-tenant welcome copy — names the actual practice-area tools and
 *  the intake → research → draft workflow Fernando's team uses daily. */
export const FERNANDO_WELCOME = [
  "Hey — I'm Prest0n. I'm here to help you and your team move faster on legal work.",
  '',
  'A few things I do well right now:',
  '• Research case law and synthesize the answer with citations',
  '• Draft documents (NDAs, demand letters, pre-hearing statements, I-589, custom)',
  '• Translate between English and Spanish',
  '• Read and summarize uploaded records and intake materials',
  '• Run multi-step tasks end-to-end (intake → research → draft) and show my reasoning',
  '',
  'Try one of these to get started:',
  ...FERNANDO_WELCOME_STARTERS.map(starterBullet),
  '',
  'Or just tell me what you need.',
].join('\n');
